import React, { FunctionComponent } from 'react';
import { classNames } from '../../utils/class-names';
import { Intent } from './intent';

export const SpinnerSize = {
  SMALL: 20,
  STANDARD: 50,
  LARGE: 100,
};

interface SpinnerProps {
  intent?: Intent;
  /** Pixel size, or the react-bootstrap 'sm' shorthand. */
  size?: number | 'sm';
  /** 'border' renders a spinning ring, 'grow' a pulsing dot. */
  animation?: 'border' | 'grow';
  className?: string;
}

const intentColor = (intent?: Intent) => {
  switch (intent) {
    case Intent.PRIMARY:
      return 'text-primary';
    case Intent.SUCCESS:
      return 'text-green-600';
    case Intent.WARNING:
      return 'text-yellow-500';
    case Intent.DANGER:
      return 'text-red-600';
    default:
      return 'text-gray-400';
  }
};

const Spinner: FunctionComponent<SpinnerProps> = ({
  intent,
  size: sizeProp = SpinnerSize.STANDARD,
  animation = 'border',
  className,
}) => {
  const size = sizeProp === 'sm' ? SpinnerSize.SMALL : sizeProp;
  if (animation === 'grow') {
    return (
      <span
        role="status"
        aria-label="Loading"
        className={classNames(
          'inline-block rounded-full bg-current animate-pulse',
          intentColor(intent),
          className || ''
        )}
        style={{ width: size, height: size }}
      />
    );
  }

  const strokeWidth = size >= SpinnerSize.STANDARD ? 4 : 3;

  return (
    <span
      role="status"
      aria-label="Loading"
      className={classNames('inline-block', className || '')}
    >
      <svg
        className={classNames('animate-spin', intentColor(intent))}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth={strokeWidth}
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </span>
  );
};

export default Spinner;
